import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5001/api/v1');

class ApiService {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('portal_token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.startTokenRefreshTimer();
    this.setupVisibilityHandler();

    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        // Add a check to ensure we don't try to refresh the token on a failed login attempt
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/login')) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                return this.axiosInstance(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const refreshToken = localStorage.getItem('portal_refresh_token');
          if (!refreshToken) {
            this.logout();
            return Promise.reject(error);
          }

          try {
            const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
            const { token: newToken, refreshToken: newRefreshToken } = data.data;
            localStorage.setItem('portal_token', newToken);
            if (newRefreshToken) {
              localStorage.setItem('portal_refresh_token', newRefreshToken);
            }
            this.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
            this.processQueue(null, newToken);
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError as AxiosError, null);
            this.logout();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: AxiosError | null, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private startTokenRefreshTimer() {
    this.clearTokenRefreshTimer();
    const token = localStorage.getItem('portal_token');
    if (token) {
      this.refreshTimer = setInterval(() => this.refreshTokenProactively(), this.REFRESH_INTERVAL);
    }
  }

  private clearTokenRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('portal_token')) {
        this.refreshTokenProactively();
      }
    });
  }

  private async refreshTokenProactively() {
    if (this.isRefreshing) return;

    const refreshToken = localStorage.getItem('portal_refresh_token');
    if (!refreshToken) return;

    this.isRefreshing = true;
    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
      const { token: newToken, refreshToken: newRefreshToken } = data.data;
      localStorage.setItem('portal_token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('portal_refresh_token', newRefreshToken);
      }
      this.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
      this.startTokenRefreshTimer();
    } catch (error) {
      this.logout();
    } finally {
      this.isRefreshing = false;
    }
  }

  private logout() {
    this.clearTokenRefreshTimer();
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_refresh_token');
    localStorage.removeItem('portal_user');
    window.location.href = '/portal';
  }

  private async request<T>(method: 'get' | 'post' | 'put' | 'delete', endpoint: string, payload?: any): Promise<T> {
    const response = await this.axiosInstance[method](endpoint, payload);
    return response.data.data || response.data;
  }

  // Auth
  async login(email: string, password: string) {
    const response = await this.request<any>('post', '/auth/login', { email, password });
    if (response.token && response.refreshToken) {
      localStorage.setItem('portal_token', response.token);
      localStorage.setItem('portal_refresh_token', response.refreshToken);
      localStorage.setItem('portal_user', JSON.stringify(response.user));
      this.startTokenRefreshTimer();
    }
    return response;
  }

  async register(email: string, password: string, displayName: string) {
    const response = await this.request<any>('post', '/auth/register', { email, password, displayName });
    if (response.token && response.refreshToken) {
      localStorage.setItem('portal_token', response.token);
      localStorage.setItem('portal_refresh_token', response.refreshToken);
      localStorage.setItem('portal_user', JSON.stringify(response.user));
      this.startTokenRefreshTimer();
    }
    return response;
  }

  async forgotPassword(email: string) {
    return this.request<any>('post', '/auth/forgot-password', { email });
  }

  async getCurrentUser() {
    return this.request<any>('get', '/user');
  }

  // Portal endpoints
  async getDashboard() {
    return this.request<any>('get', '/portal/dashboard');
  }

  async getApiKeys() {
    return this.request<any[]>('get', '/portal/keys');
  }

  async createApiKey(name: string) {
    return this.request<any>('post', '/portal/keys', { name });
  }

  async updateApiKey(keyId: string, name: string) {
    return this.request<any>('put', `/portal/keys/${keyId}`, { name });
  }

  async revokeApiKey(keyId: string) {
    return this.request<any>('delete', `/portal/keys/${keyId}`);
  }

  async getCreditBalance() {
    return this.request<any>('get', '/portal/credits');
  }

  async topUpCredits(amount: number, description?: string) {
    return this.request<any>('post', '/portal/credits/topup', { amount, description });
  }

  async createCreditCheckout(amount: number, currency: string) {
    return this.request<any>('post', '/portal/credits/checkout', { amount, currency });
  }

  async getCreditHistory(limit = 50, offset = 0) {
    return this.request<any[]>('get', `/portal/credits/history?limit=${limit}&offset=${offset}`);
  }

  async getUsageAnalytics(params?: { dateFrom?: string; dateTo?: string }) {
    const query = params && Object.keys(params).length
      ? '?' + new URLSearchParams(params as Record<string, string>).toString()
      : '';
    return this.request<any>('get', `/portal/usage${query}`);
  }

  // Category Rules (per-user, Phase 1: privacy)
  async getCategoryRules(slug: string) {
    return this.request<any>('get', `/portal/categories/${slug}/rules`);
  }

  async updateCategoryRules(slug: string, rules: string) {
    return this.request<any>('put', `/portal/categories/${slug}/rules`, { rules });
  }

  async resetCategoryToDefault(slug: string) {
    return this.request<any>('post', `/portal/categories/${slug}/reset`);
  }

  async testCategoryRules(slug: string, testQuery: string) {
    return this.request<any>('post', `/portal/categories/${slug}/test`, { testQuery });
  }

  // Convenience wrappers for privacy (Phase 1)
  async getPrivacyRules() {
    return this.getCategoryRules('privacy');
  }

  async updatePrivacyRules(rules: string) {
    return this.updateCategoryRules('privacy', rules);
  }

  async resetPrivacyRulesToDefault() {
    return this.resetCategoryToDefault('privacy');
  }

  async testPrivacyRules(testQuery: string) {
    return this.testCategoryRules('privacy', testQuery);
  }

  // Task Determinations
  async getTaskDetUsage() {
    return this.request<any>('get', '/portal/task-determinations/usage');
  }

  async getTaskDetHistory(limit = 50, offset = 0) {
    return this.request<any>('get', `/portal/task-determinations/history?limit=${limit}&offset=${offset}`);
  }

  async getTaskDetPlans() {
    return this.request<{ success: boolean; data: { plans: any[]; currentPlanId: string } }>('get', '/portal/task-determinations/plans');
  }

  async getTaskDetSubscription() {
    return this.request<any>('get', '/portal/task-determinations/subscription');
  }

  async createTaskDetSubscription(planId: string) {
    return this.request<any>('post', '/portal/task-determinations/subscribe', { planId });
  }

  async createTaskDetBillingPortal() {
    return this.request<any>('post', '/portal/task-determinations/billing-portal');
  }

  async createTaskDetCheckout(packSize?: number) {
    return this.request<any>('post', '/portal/task-determinations/checkout', packSize ? { packSize } : {});
  }

  // Generic CRUD methods
  async get(endpoint: string) {
    return this.request<any>('get', endpoint);
  }

  async post(endpoint: string, data: any) {
    return this.request<any>('post', endpoint, data);
  }

  async put(endpoint: string, data: any) {
    return this.request<any>('put', endpoint, data);
  }

  async delete(endpoint: string) {
    return this.request<any>('delete', endpoint);
  }
}

export const apiService = new ApiService();
